<script setup>
/**
 * DE ONDE VEIO o que o Lumos disse (Fase 1, 08/10/2026).
 *
 * Cada linha é um item que uma ferramenta devolveu neste turno, com a alçada
 * de quem perguntou — o servidor descarta o resto
 * (Captain::Copilot::ChatService#com_cartoes). A linha é a da paleta
 * (`.advup-paleta__*`), e o destino é o da referência do Chat interno
 * (`rotaDaReferencia`): processo, agenda e conversa abrem onde já abrem.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { rotaDaReferencia } from 'dashboard/routes/dashboard/internal-chat/useSlashSearch';

const props = defineProps({
  cartoes: { type: Array, default: () => [] },
  // Segundos, como vem do servidor (`created_at.to_i`).
  criadoEm: { type: Number, default: null },
});

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

// O ícone diz o ASSUNTO; a cor só aparece quando o item traz notícia
// (venceu, é hoje) — senão é a cor do tipo, como na paleta.
const ICONE = {
  processo: 'i-ph-scales-fill',
  agenda: 'i-ph-calendar-check-fill',
  conversa: 'i-ph-chat-circle-text-fill',
  publicacao: 'i-ph-newspaper-fill',
  contato: 'i-ph-user-circle-fill',
  email: 'i-ph-envelope-simple-fill',
};
const TOM_DO_TIPO = { processo: 'azul', agenda: 'iris' };

const linhas = computed(() =>
  props.cartoes
    .map(cartao => ({
      ...cartao,
      rota: rotaDaReferencia(cartao.tipo, cartao.id, accountScopedRoute),
      icone: ICONE[cartao.tipo],
      tom: cartao.tom || TOM_DO_TIPO[cartao.tipo],
    }))
    .filter(linha => linha.rota)
);

const hora = computed(() => {
  if (!props.criadoEm) return '';
  return new Date(props.criadoEm * 1000).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
});

// A linha acende pelo cursor e pelo Tab, como na paleta (`--ativa`).
const ativa = ref(null);
</script>

<template>
  <section class="lumos-fontes">
    <header class="lumos-fontes__cab">
      <span>{{ t('CAPTAIN.COPILOT.FONTES.TITULO') }}</span>
      <span v-if="hora" class="lumos-fontes__hora">
        {{ t('CAPTAIN.COPILOT.FONTES.DADOS_DE', { hora }) }}
      </span>
    </header>
    <ul class="lumos-fontes__lista">
      <li
        v-for="(linha, i) in linhas"
        :key="linha.ref"
        class="lumos-fontes__item"
        :style="{ '--i': i }"
      >
        <router-link
          :to="linha.rota"
          class="advup-paleta__linha lumos-fontes__linha"
          :class="{ 'advup-paleta__linha--ativa': ativa === linha.ref }"
          :aria-label="
            t('CAPTAIN.COPILOT.FONTES.ABRIR', { titulo: linha.titulo })
          "
          @mouseenter="ativa = linha.ref"
          @mouseleave="ativa = null"
          @focus="ativa = linha.ref"
          @blur="ativa = null"
        >
          <span
            class="advup-paleta__ladrilho"
            :class="linha.tom && `advup-paleta__ladrilho--${linha.tom}`"
            aria-hidden="true"
          >
            <i :class="linha.icone" />
          </span>
          <span class="advup-paleta__texto">
            <span class="advup-paleta__titulo lumos-fontes__titulo">{{
              linha.titulo
            }}</span>
            <span v-if="linha.sub" class="advup-paleta__sub">
              <span class="lumos-fontes__sub">{{ linha.sub }}</span>
            </span>
          </span>
          <i
            class="i-ph-arrow-up-right lumos-fontes__seta"
            aria-hidden="true"
          />
        </router-link>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.lumos-fontes {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
}

.lumos-fontes__cab {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--esp-2);
  padding: 0 var(--esp-1);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  line-height: var(--linha-apertada);
  color: rgb(var(--slate-11));
}

.lumos-fontes__hora {
  font-weight: var(--peso-leve);
  font-variant-numeric: tabular-nums;
}

.lumos-fontes__lista {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  margin: 0;
  padding: var(--esp-1);
  list-style: none;
  border-radius: var(--r-4);
  background: rgb(var(--solid-1));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-4));
}

.lumos-fontes__item {
  animation: lumos-fonte-chega var(--mov-3) var(--mov-curva)
    calc(var(--i, 0) * var(--cascata-passo)) backwards;
}

.lumos-fontes__linha {
  min-height: 48px;
  color: inherit;
  text-decoration: none;
}

.lumos-fontes__linha:focus-visible {
  outline: 2px solid rgb(var(--blue-9));
  outline-offset: 1px;
}

/* No painel de 380px uma linha só cortava o nome do prazo no meio; duas
   linhas dizem o que é sem abrir. */
.lumos-fontes__titulo {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
}

.lumos-fontes__sub {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A seta anda quando a linha acende: diz que abre em outro lugar. */
.lumos-fontes__seta {
  flex-shrink: 0;
  font-size: var(--ico-2);
  color: rgb(var(--slate-9));
  opacity: 0;
  transform: translate(-3px, 3px);
  transition:
    opacity var(--mov-2) var(--mov-curva),
    transform var(--mov-2) var(--mov-curva),
    color var(--mov-2) var(--mov-curva);
}

.advup-paleta__linha--ativa .lumos-fontes__seta {
  opacity: 1;
  transform: translate(0, 0);
  color: rgb(var(--blue-10));
}

@keyframes lumos-fonte-chega {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
}

@media (hover: none) {
  .lumos-fontes__seta {
    opacity: 1;
    transform: none;
  }
}
</style>
